import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { commitSha } from '@environments/build-info';
import { environment } from '@environments/environment';
import { context, Span, SpanStatusCode, trace, Tracer } from '@opentelemetry/api';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME, ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import {
  BatchSpanProcessor,
  ReadableSpan,
  SpanProcessor,
  StackContextManager,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';

const HONEYCOMB_OTLP_TRACES_URL = 'https://api.honeycomb.io/v1/traces';
const SERVICE_NAME = 'neo-genesis-portal';

/**
 * Stamps the currently signed-in Neo staff member and the current route onto every
 * span at creation time - including spans StackContextManager can't otherwise link
 * to a route (see instrumentRouter). Not built on OTel Baggage/Context: this is
 * ambient session state read fresh per span, not something propagated through a
 * call chain.
 */
class SessionAttributesSpanProcessor implements SpanProcessor {
  public constructor(private readonly getAttributes: () => Record<string, string | undefined>) {}

  public onStart(span: Span): void {
    for (const [key, value] of Object.entries(this.getAttributes())) {
      if (value) {
        span.setAttribute(key, value);
      }
    }
  }

  public onEnd(_span: ReadableSpan): void {}
  public shutdown(): Promise<void> { return Promise.resolve(); }
  public forceFlush(): Promise<void> { return Promise.resolve(); }
}

@Injectable({
  providedIn: 'root',
})
export class OtelService {

  private tracer: Tracer | undefined;
  private currentUserId: string | undefined;
  private router: Router | undefined;

  /** Called once auth resolves (App) so subsequent spans carry the signed-in user. */
  public setCurrentUserId(userId: string | undefined): void {
    this.currentUserId = userId;
  }

  public initialise(): void {
    try {
      const apiKey = environment.honeycombApiKey;
      if (!apiKey) {
        return;
      }

      const exporter = new OTLPTraceExporter({
        url: HONEYCOMB_OTLP_TRACES_URL,
        headers: { 'x-honeycomb-team': apiKey },
      });

      const resourceAttributes: Record<string, string> = { [ATTR_SERVICE_NAME]: SERVICE_NAME };
      if (environment.environmentName) {
        resourceAttributes[ATTR_DEPLOYMENT_ENVIRONMENT_NAME] = environment.environmentName;
      }
      if (commitSha) {
        resourceAttributes[ATTR_SERVICE_VERSION] = commitSha;
      }

      const provider = new WebTracerProvider({
        resource: resourceFromAttributes(resourceAttributes),
        // Order matters: must run before the batch/export processor so the attributes
        // it sets are present on the span by the time that processor reads it.
        spanProcessors: [
          new SessionAttributesSpanProcessor(() => ({
            'enduser.id': this.currentUserId,
            'app.route': this.router?.url,
          })),
          new BatchSpanProcessor(exporter),
        ],
      });

      // Portal is a zoneless Angular app (provideZonelessChangeDetection, no zone.js
      // polyfill) - ZoneContextManager would pull zone.js in as a side effect and break
      // that. StackContextManager needs no zone.js; the tradeoff is it only tracks
      // context synchronously, so a span opened before an `await`/`.then()` boundary
      // won't automatically parent work resumed after it.
      provider.register({ contextManager: new StackContextManager() });

      registerInstrumentations({
        instrumentations: [
          new DocumentLoadInstrumentation(),
          new UserInteractionInstrumentation(),
          new XMLHttpRequestInstrumentation({
            propagateTraceHeaderCorsUrls: [new RegExp(`^${escapeRegExp(environment.api.baseUrl)}`)],
            // Unlike the backend's ASP.NET Core/HttpClient instrumentation, this package does not
            // mark a span as an error for a 4xx/5xx response by default - without this, this
            // dataset has no error column at all for Honeycomb's "Configure error" to use.
            applyCustomAttributesOnSpan: (span, xhr) => {
              if (xhr.status >= 400) {
                span.setStatus({ code: SpanStatusCode.ERROR });
                span.setAttribute('error', true);
              }
            },
          }),
        ],
      });

      this.tracer = trace.getTracer(SERVICE_NAME);
    }
    catch (e) {
      // Ignore telemetry errors to prevent app failures.
    }
  }

  /**
   * Records an uncaught application exception (called from NeoGlobalErrorHandler).
   * Attaches it to the active span if one exists, otherwise creates a standalone
   * span so the exception still reaches Honeycomb with the signed-in user's context.
   */
  public recordException(error: unknown): void {
    if (!this.tracer) {
      return;
    }
    const activeSpan = trace.getActiveSpan();
    if (activeSpan) {
      activeSpan.recordException(error as Error);
      activeSpan.setStatus({ code: SpanStatusCode.ERROR });
      return;
    }
    const span = this.tracer.startSpan('uncaught-exception');
    span.recordException(error as Error);
    span.setStatus({ code: SpanStatusCode.ERROR });
    span.end();
  }

  /**
   * Wraps Router.navigateByUrl so a route's whole navigation - guards, resolvers, the
   * target component's construction/ngOnInit, and anything ngOnInit synchronously kicks
   * off (its XHRs included) - runs inside that route's span. Router.navigate() and
   * RouterLink both resolve through this method internally, so patching it is a single
   * choke point covering all in-app navigation.
   */
  public instrumentRouter(router: Router): void {
    // Read by SessionAttributesSpanProcessor regardless of whether the tracer is
    // active below, so the field is never left stale for a later initialise().
    this.router = router;
    if (!this.tracer) {
      return;
    }
    const tracer = this.tracer;
    const originalNavigateByUrl = router.navigateByUrl.bind(router);
    router.navigateByUrl = (url: Parameters<Router['navigateByUrl']>[0], extras?: Parameters<Router['navigateByUrl']>[1]) => {
      const urlString = url.toString();
      const span = tracer.startSpan(`route-change ${urlString}`);
      span.setAttribute('app.route', urlString);
      const ctx = trace.setSpan(context.active(), span);
      return context.with(ctx, () => originalNavigateByUrl(url, extras))
        .then((result) => {
          span.end();
          return result;
        })
        .catch((error: unknown) => {
          span.recordException(error as Error);
          span.setStatus({ code: SpanStatusCode.ERROR });
          span.end();
          throw error;
        });
    };
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
